import { LocalStorageKey } from "@/utils/local-storage-utils";

/** The catalog census staleness the UI asks for by default, as
 *  GetSourceSnapshotRequest.max_staleness. See CENSUS_MAX_STALENESS_OPTIONS. */
export const DEFAULT_CENSUS_MAX_STALENESS_SECONDS = 24 * 60 * 60;

export interface PersistedAdminOptions {
  useJsonGrpcTransport: boolean;
  logGrpcResponses: boolean;
  limitedPerformanceMode: boolean;
  disableNetworkLog: boolean;
  /** How stale a stored census the catalog UI will accept, in seconds.
   *
   *  Must never be 0: the proto treats absent and zero alike as "force a live census", so a
   *  zero here would mean the opposite of the "accept anything" it reads like. The widest
   *  option is a very large duration instead. */
  censusMaxStalenessSeconds: number;
}

function getPersistedAdminOptions(): PersistedAdminOptions | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const savedValue = localStorage.getItem(LocalStorageKey.ADMIN_OPTIONS);
  if (savedValue) {
    try {
      return JSON.parse(savedValue) as PersistedAdminOptions;
    } catch (err) {
      console.error(
        `[Chalk] Local storage key ${LocalStorageKey.ADMIN_OPTIONS} failed to parse with error: ${err}`,
      );
    }
  }
  return undefined;
}

export function getPersistedAdminOptionsWithDefaults(): PersistedAdminOptions {
  const persisted = getPersistedAdminOptions();
  return {
    useJsonGrpcTransport: persisted?.useJsonGrpcTransport ?? false,
    logGrpcResponses: persisted?.logGrpcResponses ?? false,
    limitedPerformanceMode: persisted?.limitedPerformanceMode ?? false,
    disableNetworkLog: persisted?.disableNetworkLog ?? false,
    // A persisted 0 predates this option or was hand-edited; either way it would force a live
    // census on every catalog load, so fall back to the default rather than honour it.
    censusMaxStalenessSeconds:
      persisted?.censusMaxStalenessSeconds != null && persisted.censusMaxStalenessSeconds > 0
        ? persisted.censusMaxStalenessSeconds
        : DEFAULT_CENSUS_MAX_STALENESS_SECONDS,
  };
}

export function persistAdminOptions(opts: PersistedAdminOptions) {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(LocalStorageKey.ADMIN_OPTIONS, JSON.stringify(opts));
}

export function updatePersistedAdminOptions(opts: Partial<PersistedAdminOptions>) {
  const current = getPersistedAdminOptionsWithDefaults();
  const updated = {
    ...current,
    ...opts,
  };
  persistAdminOptions(updated);
}
